// MapView — the core map screen
// MapLibre GL JS + PMTiles (OPFS) + OpenFreeMap style
// GPS marker + waypoint pins + catch pins

import { useEffect, useRef, useState, useCallback } from 'react';
import maplibregl from 'maplibre-gl';
import { Protocol } from 'pmtiles';
import 'maplibre-gl/dist/maplibre-gl.css';

import { useGps } from '@/lib/gps';
import { hasTileFile, getTileFile, MAINE_TILE_KEY } from '@/lib/opfs-tiles';
import type { Waypoint, Catch } from '@/lib/world-model';
import type { SpotData } from '@/data/spot';

// ─── OpenFreeMap style (Liberty) — served from CDN when online,
//     falls back to a minimal style when offline ────────────────────────────────
const OPENFREEMAP_STYLE = 'https://tiles.openfreemap.org/styles/liberty';

const OFFLINE_STYLE: maplibregl.StyleSpecification = {
  version: 8,
  name: 'BluGuide Offline',
  sources: {},
  layers: [
    { id: 'background', type: 'background', paint: { 'background-color': '#1a2a1a' } },
  ],
};

interface MapViewProps {
  waypoints?: Waypoint[];
  catches?: Catch[];
  spots?: SpotData[];
  onMapClick?: (lat: number, lng: number) => void;
  onWaypointClick?: (waypoint: Waypoint) => void;
  onCatchClick?: (c: Catch) => void;
  onSpotClick?: (spot: SpotData) => void;
  center?: [number, number]; // [lng, lat]
  zoom?: number;
}

// Southern Maine default center
const DEFAULT_CENTER: [number, number] = [-70.4617, 43.8231];
const DEFAULT_ZOOM = 10;

export function MapView({
  waypoints = [],
  catches = [],
  spots = [],
  onMapClick,
  onWaypointClick,
  onCatchClick,
  onSpotClick,
  center = DEFAULT_CENTER,
  zoom = DEFAULT_ZOOM,
}: MapViewProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const gpsMarkerRef = useRef<maplibregl.Marker | null>(null);
  const spotMarkersRef = useRef<maplibregl.Marker[]>([]);
  const { position, error: gpsError } = useGps();
  const [mapReady, setMapReady] = useState(false);
  const [offlineAvailable, setOfflineAvailable] = useState(false);

  // ── Register PMTiles protocol once ──────────────────────────────────────────
  useEffect(() => {
    const protocol = new Protocol();
    maplibregl.addProtocol('pmtiles', protocol.tile);
    return () => { maplibregl.removeProtocol('pmtiles'); };
  }, []);

  // ── Check if offline tiles are available ────────────────────────────────────
  useEffect(() => {
    hasTileFile(MAINE_TILE_KEY).then(setOfflineAvailable);
  }, []);

  // ── Initialize the map ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const init = async () => {
      let style: string | maplibregl.StyleSpecification = OFFLINE_STYLE;

      if (offlineAvailable) {
        // Build a style that points at the local OPFS file
        try {
          const file = await getTileFile(MAINE_TILE_KEY);
          const fileUrl = URL.createObjectURL(file);
          style = {
            version: 8,
            name: 'BluGuide Offline',
            sources: {
              'maine-tiles': {
                type: 'vector',
                url: `pmtiles://${fileUrl}`,
                attribution: '© OpenStreetMap contributors',
              },
            },
            layers: [
              { id: 'background', type: 'background', paint: { 'background-color': '#e8f0e8' } },
              {
                id: 'water',
                type: 'fill',
                source: 'maine-tiles',
                'source-layer': 'water',
                paint: { 'fill-color': '#4a90d9', 'fill-opacity': 0.8 },
              },
              {
                id: 'waterway',
                type: 'line',
                source: 'maine-tiles',
                'source-layer': 'waterway',
                paint: { 'line-color': '#4a90d9', 'line-width': 1.5 },
              },
              {
                id: 'landuse-forest',
                type: 'fill',
                source: 'maine-tiles',
                'source-layer': 'landuse',
                filter: ['==', 'class', 'forest'],
                paint: { 'fill-color': '#2d5a27', 'fill-opacity': 0.4 },
              },
              {
                id: 'road',
                type: 'line',
                source: 'maine-tiles',
                'source-layer': 'transportation',
                paint: { 'line-color': '#888', 'line-width': 1 },
              },
              {
                id: 'place-label',
                type: 'symbol',
                source: 'maine-tiles',
                'source-layer': 'place',
                layout: {
                  'text-field': ['get', 'name'],
                  'text-size': 12,
                  'text-font': ['Noto Sans Regular'],
                },
                paint: { 'text-color': '#1c1c1e' },
              },
            ],
          };
        } catch {
          // PMTiles unavailable — minimal offline fallback
          style = OFFLINE_STYLE;
        }
      } else if (navigator.onLine) {
        style = OPENFREEMAP_STYLE;
      }

      const map = new maplibregl.Map({
        container: containerRef.current!,
        style,
        center,
        zoom,
        attributionControl: { compact: true },
      });

      map.addControl(new maplibregl.NavigationControl({ showCompass: true }), 'top-right');

      map.on('load', () => setMapReady(true));

      map.on('click', (e) => {
        onMapClick?.(e.lngLat.lat, e.lngLat.lng);
      });

      mapRef.current = map;
    };

    init();

    return () => {
      mapRef.current?.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offlineAvailable]);

  // ── GPS marker ───────────────────────────────────────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady || !position) return;

    if (!gpsMarkerRef.current) {
      const el = document.createElement('div');
      el.className = 'gps-dot';
      el.style.cssText = `
        width: 16px; height: 16px; border-radius: 50%;
        background: #007AFF; border: 3px solid #fff;
        box-shadow: 0 0 0 4px rgba(0,122,255,0.25);
      `;
      gpsMarkerRef.current = new maplibregl.Marker({ element: el })
        .setLngLat([position.lng, position.lat])
        .addTo(map);
    } else {
      gpsMarkerRef.current.setLngLat([position.lng, position.lat]);
    }
  }, [position, mapReady]);

  // ── Waypoint pins ────────────────────────────────────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;

    // Remove existing waypoint source/layer and re-add
    if (map.getSource('waypoints')) map.removeLayer('waypoints-layer');
    if (map.getSource('waypoints')) map.removeSource('waypoints');

    if (waypoints.length === 0) return;

    const typeColor: Record<string, string> = {
      hotspot: '#1a4a2e',
      hazard: '#d45a1a',
      access: '#007AFF',
      camp: '#8e44ad',
      general: '#8e8e93',
    };

    map.addSource('waypoints', {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: waypoints.map(w => ({
          type: 'Feature',
          geometry: { type: 'Point', coordinates: [w.lng, w.lat] },
          properties: { id: w.id, label: w.label, type: w.type, color: typeColor[w.type] ?? '#8e8e93' },
        })),
      },
    });

    map.addLayer({
      id: 'waypoints-layer',
      type: 'circle',
      source: 'waypoints',
      paint: {
        'circle-radius': 8,
        'circle-color': ['get', 'color'],
        'circle-stroke-width': 2,
        'circle-stroke-color': '#fff',
      },
    });

    map.on('click', 'waypoints-layer', (e) => {
      const feature = e.features?.[0];
      if (!feature) return;
      const found = waypoints.find(w => w.id === feature.properties.id);
      if (found) onWaypointClick?.(found);
    });
  }, [waypoints, mapReady, onWaypointClick]);

  // ── Catch pins ───────────────────────────────────────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;

    if (map.getLayer('catches-layer')) map.removeLayer('catches-layer');
    if (map.getSource('catches')) map.removeSource('catches');

    if (catches.length === 0) return;

    map.addSource('catches', {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: catches.map(c => ({
          type: 'Feature',
          geometry: { type: 'Point', coordinates: [c.lng, c.lat] },
          properties: { id: c.id, species: c.speciesName ?? 'Unknown' },
        })),
      },
    });

    map.addLayer({
      id: 'catches-layer',
      type: 'circle',
      source: 'catches',
      paint: {
        'circle-radius': 6,
        'circle-color': '#FFD700',
        'circle-stroke-width': 2,
        'circle-stroke-color': '#fff',
      },
    });
  }, [catches, mapReady]);

  // ── Center on GPS when first fix arrives ─────────────────────────────────────
  const centeredRef = useRef(false);
  useEffect(() => {
    if (!mapRef.current || !mapReady || !position || centeredRef.current) return;
    mapRef.current.flyTo({ center: [position.lng, position.lat], zoom: 14, duration: 1200 });
    centeredRef.current = true;
  }, [position, mapReady]);

  // ── Spot markers ─────────────────────────────────────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;

    // Remove previous markers
    spotMarkersRef.current.forEach(m => m.remove());
    spotMarkersRef.current = [];

    spots.forEach(spot => {
      const el = document.createElement('div');
      el.title = spot.name;
      el.style.cssText = [
        'width:36px', 'height:36px', 'border-radius:50%',
        'background:#1a4a2e', 'border:2.5px solid #4cd964',
        'display:flex', 'align-items:center', 'justify-content:center',
        'cursor:pointer', 'box-shadow:0 2px 12px rgba(0,0,0,0.6)',
        'font-size:18px', 'line-height:1', 'user-select:none',
        'transition:transform 0.15s',
      ].join(';');
      el.textContent = '🐟';
      el.addEventListener('mouseenter', () => { el.style.transform = 'scale(1.15)'; });
      el.addEventListener('mouseleave', () => { el.style.transform = 'scale(1)'; });
      el.addEventListener('click', e => {
        e.stopPropagation();
        onSpotClick?.(spot);
      });

      const marker = new maplibregl.Marker({ element: el })
        .setLngLat([spot.coordinates.lng, spot.coordinates.lat])
        .addTo(map);
      spotMarkersRef.current.push(marker);
    });

    return () => {
      spotMarkersRef.current.forEach(m => m.remove());
      spotMarkersRef.current = [];
    };
  }, [spots, mapReady, onSpotClick]);

  return (
    <div className="relative w-full h-full">
      <div ref={containerRef} className="w-full h-full" />

      {/* Offline indicator */}
      {!navigator.onLine && (
        <div className="absolute top-3 left-3 bg-[#d45a1a] text-white text-xs font-semibold px-3 py-1 rounded-full shadow">
          ● Offline
        </div>
      )}

      {/* GPS accuracy badge */}
      {position && (
        <div className="absolute bottom-24 left-3 bg-black/60 backdrop-blur text-white text-xs px-2 py-1 rounded-lg">
          ±{Math.round(position.accuracy)}m
        </div>
      )}

      {gpsError && (
        <div className="absolute bottom-24 left-3 bg-[#d45a1a]/80 backdrop-blur text-white text-xs px-2 py-1 rounded-lg">
          GPS: {gpsError}
        </div>
      )}
    </div>
  );
}
